import React, { useCallback, useEffect, useState } from "react";
import { getCsrfToken } from "../../../../../public/api/authApi";
import GlobalDataTable from "../../../../../global/GlobalDataTable";
import GlobalBadge from "../../../../../global/GlobalBadge";
import GlobalFeedback from "../../../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../../../global/GlobalSuccessFlashModal";
import { GlobalModal } from "../../../../../global/GlobalModal";
import { GlobalButton } from "../../../../../global/forms/GlobalFormFields";
import PriorityTypeForm from "./PriorityTypeForm";
import {
  getPriorityTypes,
  createPriorityType,
  updatePriorityType,
  deletePriorityType,
} from "../../../api/priorityTypeApi";
import { useScheduleRole } from "../../../utils/useScheduleRole";
import { isScheduleMasterStatusActive } from "../../../utils/scheduleMasterStatusOptions";
import { ensureCsrfToken, safeErrorText } from "../../../utils/scheduleHelpers";

export default function PriorityTypeMasterSection({ showInactive = false }) {
  const perms = useScheduleRole();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getPriorityTypes();
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(showInactive ? list : list.filter((r) => Number(r.is_active) !== 2));
    } catch (err) {
      setError(safeErrorText(err, "Failed to load priority types"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (body) => {
    setSubmitting(true);
    setFormError("");
    try {
      const csrf = await ensureCsrfToken(getCsrfToken);
      const res = await createPriorityType(body, csrf);
      setSuccessMsg(res?.message || "Priority type created successfully");
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(safeErrorText(err));
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (body) => {
    if (!editing?.id) return;
    setSubmitting(true);
    setFormError("");
    try {
      const csrf = await ensureCsrfToken(getCsrfToken);
      const res = await updatePriorityType(editing.id, body, csrf);
      setSuccessMsg(res?.message || "Priority type updated successfully");
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(safeErrorText(err));
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      const csrf = await ensureCsrfToken(getCsrfToken);
      const res = await deletePriorityType(deleteTarget.id, csrf);
      setSuccessMsg(res?.message || "Priority type deleted successfully");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setError(safeErrorText(err, "Failed to delete priority type"));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <p className="schedule-table-loading">Loading priority types…</p>;
  }

  return (
    <div className="ub-master-section">
      <GlobalFeedback type="error" text={error} role="alert" />
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />

      {perms.canManageMasters ? (
        <PriorityTypeForm
          mode={editing ? "edit" : "create"}
          initialValues={
            editing
              ? {
                  priority_name: editing.priority_name ?? "",
                  status_id:
                    editing.status_id != null && editing.status_id !== ""
                      ? String(editing.status_id)
                      : "1",
                }
              : undefined
          }
          onSubmit={editing ? handleUpdate : handleCreate}
          onCancel={editing ? () => setEditing(null) : undefined}
          submitting={submitting}
          serverError={formError}
        />
      ) : (
        <p className="visitor-workspace__placeholder" style={{ marginBottom: "1rem" }}>
          You have view-only access to priority types.
        </p>
      )}

      <GlobalDataTable
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel="Priority types"
        headers={[
          { key: "priority_name", label: "Priority name" },
          { key: "status", label: "Status" },
          { key: "created_at", label: "Created" },
          { key: "actions", label: "Actions", className: "ub-table__col-actions" },
        ]}
      >
        {rows.map((row) => {
          const active = isScheduleMasterStatusActive(row);
          return (
            <tr key={row.id}>
              <td style={{ fontWeight: 500 }}>{row.priority_name}</td>
              <td>
                <GlobalBadge active={active} activeText="ACTIVE" inactiveText="INACTIVE" />
              </td>
              <td>{row.created_at ? new Date(row.created_at).toLocaleDateString() : "—"}</td>
              <td className="ub-table__col-actions">
                {perms.canManageMasters ? (
                  <div className="ub-table-actions">
                    <GlobalButton variant="ghost" size="sm" onClick={() => setEditing(row)}>
                      Edit
                    </GlobalButton>
                    {perms.canDeleteMasters ? (
                      <GlobalButton variant="danger" size="sm" onClick={() => setDeleteTarget(row)}>
                        Delete
                      </GlobalButton>
                    ) : null}
                  </div>
                ) : (
                  "—"
                )}
              </td>
            </tr>
          );
        })}
        {rows.length === 0 ? (
          <tr>
            <td colSpan={4} style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}>
              No priority types found
            </td>
          </tr>
        ) : null}
      </GlobalDataTable>

      <GlobalModal
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        title="Delete priority type"
        size="sm"
        footer={
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <GlobalButton variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </GlobalButton>
            <GlobalButton variant="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting…" : "Delete"}
            </GlobalButton>
          </div>
        }
      >
        <p>
          Soft-delete priority <strong>{deleteTarget?.priority_name}</strong>?
        </p>
      </GlobalModal>
    </div>
  );
}
